import { Fragment, MouseEvent, useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import PublishIcon from "@mui/icons-material/Publish"
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline"
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import {
  LeadRoutingClient,
  RecentRoutedLead,
  RuleDistribution,
  RuleLeadCounts,
} from "@clients/leadRoutingClient"
import { relativeTime, ValuesChip } from "./freshness"
import { HubSpotLink } from "./hubspotLink"
import {
  deleteRule,
  publishRule,
  setRuleDisabled,
  reorderRules,
  selectRoutingConsultants,
  selectRules,
} from "@features/lead-routing/leadRouting.slice"
import {
  Rule,
  RuleCondition,
  RuleConditionOperator,
  RuleWeight,
  ConsultantSummary,
} from "@features/lead-routing/leadRouting.types"
import { RuleEditorDialog } from "./ruleEditorDialog"
import {
  formatConditionValue,
  humanizePropertyKey,
  PROGRAM_LABEL,
} from "./leadRoutingLabels"
import { splitHue, SplitHue, chipTintSx } from "./leadRoutingUi"
import { resolveRuleSplit } from "./ruleMembership"
import { SplitBars, SplitBarRow } from "./splitBars"

const PROPERTY_LABEL: Record<string, string> = {
  qtf: "QTF (campus score)",
  program: "Program",
  englishLevel: "English",
  germanLevel: "German",
  residency: "Residency",
  techExperience: "Tech exp",
  advisorMeeting: "Advisor mtg",
  afaRegistration: "Registered with AfA",
  utm: "UTM",
}

const OPERATOR_WORD: Record<RuleConditionOperator, string> = {
  in: "is any of",
  notIn: "is none of",
  eq: "is",
  neq: "is not",
  gt: "over",
  gte: "at least",
  lt: "under",
  lte: "at most",
  range: "between",
  contains: "contains",
  notContains: "excludes",
  isKnown: "is set",
  isUnknown: "is not set",
}

function conditionText(condition: RuleCondition): string {
  const label =
    PROPERTY_LABEL[condition.property] ??
    humanizePropertyKey(condition.property)
  const head = `${label} ${OPERATOR_WORD[condition.operator]}`
  if (condition.operator === "isKnown" || condition.operator === "isUnknown") {
    return head
  }
  if (Array.isArray(condition.value)) {
    const parts = condition.value.map((v) =>
      formatConditionValue(condition.property, v),
    )
    return `${head} ${parts.join(", ")}`
  }
  if (typeof condition.value === "object") {
    const { min, max } = condition.value
    return `${head} ${formatConditionValue(
      condition.property,
      min,
    )}–${formatConditionValue(condition.property, max)}`
  }
  return `${head} ${formatConditionValue(condition.property, condition.value)}`
}

function statusChip(rule: Rule) {
  if (rule.disabled) {
    return <Chip label="Off" size="small" sx={chipTintSx("neutral")} />
  }
  if (!rule.published) {
    return <Chip label="Draft" size="small" sx={chipTintSx("warn")} />
  }
  if (rule.hasUnpublishedChanges) {
    return (
      <Chip
        label="Live · unpublished edits"
        size="small"
        sx={chipTintSx("neutral")}
      />
    )
  }
  return <Chip label="Live" size="small" sx={chipTintSx("good")} />
}

// A segment narrower than this can't fit "Name 12%" without clipping, so its
// label is suppressed on the bar. When any segment is that thin (a rule split
// across many consultants), a legend below names every consultant and share.
const INLINE_LABEL_MIN_PCT = 14

function SplitBar(props: {
  weights: RuleWeight[]
  consultantName: (id: string) => string
  colorFor: (id: string) => SplitHue
  faded?: boolean
}) {
  const total = props.weights.reduce((sum, w) => sum + w.weight, 0) || 1
  const parts = props.weights.map((w) => ({
    consultantId: w.consultantId,
    pct: (w.weight / total) * 100,
    hue: props.colorFor(w.consultantId),
    name: props.consultantName(w.consultantId),
  }))
  const needsLegend = parts.some((p) => p.pct < INLINE_LABEL_MIN_PCT)
  return (
    <Box>
      <Box sx={{ display: "flex", gap: "4px", height: 30 }}>
        {parts.map((p) => (
          <Box
            key={p.consultantId}
            title={`${p.name}, goal ${Math.round(p.pct)}% of this rule's split`}
            sx={{
              width: `${p.pct}%`,
              opacity: props.faded ? 0.7 : 1,
              bgcolor: "background.paper",
              color: p.hue.text,
              borderStyle: "solid",
              borderWidth: "1px",
              borderColor: "divider",
              borderBottomWidth: "3px",
              borderBottomColor: p.hue.line,
              borderRadius: "6px 6px 4px 4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 400,
              fontVariantNumeric: "tabular-nums",
              overflow: "hidden",
              whiteSpace: "nowrap",
              minWidth: 0,
              px: 0.75,
              boxSizing: "border-box",
            }}
          >
            {p.pct >= INLINE_LABEL_MIN_PCT
              ? `${p.name.split(" ")[0]} ${Math.round(p.pct)}%`
              : ""}
          </Box>
        ))}
      </Box>
      {needsLegend && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 12px",
            mt: 0.75,
            opacity: props.faded ? 0.7 : 1,
          }}
        >
          {parts.map((p) => (
            <Stack
              key={p.consultantId}
              direction="row"
              alignItems="center"
              spacing={0.5}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "2px",
                  bgcolor: p.hue.line,
                  flex: "none",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {p.name.split(" ")[0]} {Math.round(p.pct)}%
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  )
}

// The real split a rule produced over the range: each consultant's goal share
// (from the rule's weights) next to the share they actually got, from the log.
// The same shape as the editor preview, but real outcomes rather than a replay.
// Rotation (fallback) has no goal, so only the actual share shows.
function RealSplitView(props: {
  ruleId: string
  from: string
  to: string
  weights: RuleWeight[]
  consultantName: (id: string) => string
}) {
  const [data, setData] = useState<RuleDistribution | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    LeadRoutingClient.getRuleDistribution(props.ruleId, props.from, props.to)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch(() => {
        if (alive) setData(null)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [props.ruleId, props.from, props.to])

  if (loading) {
    return null
  }
  // No leads landed on this rule in the range: say so, rather than showing an
  // empty area, so it's clear there's just no data yet (not a broken view).
  if (!data || data.total === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
        No leads routed here in this range yet.
      </Typography>
    )
  }

  const goalTotal = props.weights.reduce((sum, w) => sum + w.weight, 0)
  const goalById = new Map(props.weights.map((w) => [w.consultantId, w.weight]))
  const gotById = new Map<string, number>()
  const dbNameById = new Map<string, string>()
  for (const entry of data.consultants) {
    if (!entry.consultantId) continue
    gotById.set(entry.consultantId, entry.count)
    if (entry.consultantName)
      dbNameById.set(entry.consultantId, entry.consultantName)
  }
  const round1 = (value: number) => Math.round(value * 10) / 10
  const ids = [...new Set([...goalById.keys(), ...gotById.keys()])]
  const rows: SplitBarRow[] = ids
    .map((id) => {
      const got = gotById.get(id) ?? 0
      const resolved = props.consultantName(id)
      const goalPct =
        goalTotal > 0
          ? round1(((goalById.get(id) ?? 0) / goalTotal) * 100)
          : null
      const gotPct = round1((got / data.total) * 100)
      return {
        id,
        name:
          resolved === "Unknown" ? dbNameById.get(id) ?? resolved : resolved,
        targetPct: goalPct,
        actualPct: gotPct,
        got,
        barTooltip:
          goalPct !== null
            ? `Real result. Goal ${goalPct}%; over this range they got ${gotPct}% (${got} lead${
                got === 1 ? "" : "s"
              }).`
            : `Over this range they got ${gotPct}% (${got} lead${
                got === 1 ? "" : "s"
              }).`,
        deltaTooltip:
          goalPct !== null
            ? `${Math.abs(round1(gotPct - goalPct))} percentage points ${
                gotPct - goalPct >= 0 ? "above" : "below"
              } their goal share, over this range.`
            : undefined,
      }
    })
    .sort((a, b) => b.got - a.got)

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 1,
          textTransform: "uppercase",
          letterSpacing: ".05em",
        }}
      >
        Real split · {data.total} lead{data.total === 1 ? "" : "s"}
      </Typography>
      <SplitBars rows={rows} />
    </Box>
  )
}

// The real split is the headline; the lead list is secondary, so it sits behind
// a "Show routed leads" disclosure to keep the expanded rule uncluttered.
function RoutedLeadsDisclosure(props: {
  ruleId: string
  from: string
  to: string
}) {
  const [open, setOpen] = useState(false)
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        onClick={() => setOpen((prev) => !prev)}
        startIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform .2s",
            }}
          />
        }
        sx={{ textTransform: "none", color: "text.secondary" }}
      >
        {open ? "Hide routed leads" : "Show routed leads"}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <RoutedLeadsForRule
            ruleId={props.ruleId}
            from={props.from}
            to={props.to}
          />
        </Box>
      </Collapse>
    </Box>
  )
}

// The real leads a rule took, or the ones that fell through to Everyone else
// (ruleId="fallback"), over the same counting range as the took-% number.
// These are logged outcomes, never a simulation.
function RoutedLeadsForRule(props: {
  ruleId: string
  from: string
  to: string
}) {
  const [loading, setLoading] = useState(true)
  const [leads, setLeads] = useState<RecentRoutedLead[]>([])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setFailed(false)
    LeadRoutingClient.listRoutedLeadsForRule(props.ruleId, props.from, props.to)
      .then((rows) => {
        if (alive) setLeads(rows)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [props.ruleId, props.from, props.to])

  if (loading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={18} />
      </Stack>
    )
  }
  if (failed) {
    return (
      <Typography variant="caption" color="text.secondary">
        Couldn’t load these leads.
      </Typography>
    )
  }
  if (leads.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No leads in this range.
      </Typography>
    )
  }
  return (
    <Table size="small" sx={{ "& td, & th": { py: 0.5, fontSize: "0.8rem" } }}>
      <TableHead>
        <TableRow>
          <TableCell>Routed</TableCell>
          <TableCell>Lead</TableCell>
          <TableCell>Consultant</TableCell>
          <TableCell>QTF</TableCell>
          <TableCell>Program</TableCell>
          <TableCell>Values used</TableCell>
          <TableCell padding="checkbox" />
        </TableRow>
      </TableHead>
      <TableBody>
        {leads.map((lead) => (
          <TableRow key={`${lead.leadId}-${lead.routedAt}`}>
            <TableCell>{relativeTime(lead.routedAt)}</TableCell>
            <TableCell>{lead.leadName ?? "-"}</TableCell>
            <TableCell>{lead.consultantName ?? "-"}</TableCell>
            <TableCell>{String(lead.snapshot.qtfScore ?? "-")}</TableCell>
            <TableCell>
              {PROGRAM_LABEL[String(lead.snapshot.fieldOfStudyInterest)] ??
                String(lead.snapshot.fieldOfStudyInterest ?? "-")}
            </TableCell>
            <TableCell>
              <ValuesChip
                source={lead.snapshot.propertiesSource as string | undefined}
                syncedAt={
                  lead.snapshot.propertiesSyncedAt as string | undefined
                }
              />
            </TableCell>
            <TableCell padding="checkbox">
              <HubSpotLink email={lead.email} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function SortableRuleCard(props: {
  rule: Rule
  index: number
  took?: number
  total: number
  from: string
  to: string
  consultantName: (id: string) => string
  ruleName: (id: string) => string
  colorFor: (id: string) => SplitHue
  consultants: ConsultantSummary[]
  onEdit: (rule: Rule) => void
  onDelete: (rule: Rule) => void
  onPublish: (id: string) => void
  onToggleDisabled: (rule: Rule) => void
}) {
  const { rule, index, took, total } = props
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id })
  const [open, setOpen] = useState(false)
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  const stop = (e: MouseEvent) => e.stopPropagation()

  return (
    <Stack ref={setNodeRef} style={style} direction="row" mb={1.5}>
      {/* A switched-off rule has no position in the match order: no number,
          no drag handle, just the card. */}
      {rule.disabled ? null : (
        <Stack alignItems="center" sx={{ width: 40, pt: 1.5 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1.5,
              bgcolor: "text.primary",
              color: "background.paper",
              display: "grid",
              placeItems: "center",
              fontSize: 12,
              fontWeight: 400,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {index + 1}
          </Box>
          <IconButton
            size="small"
            sx={{ cursor: "grab", mt: 0.25 }}
            {...attributes}
            {...listeners}
            onClick={stop}
            aria-label="drag to reorder"
          >
            <DragIndicatorIcon fontSize="small" color="disabled" />
          </IconButton>
          <Box
            sx={{
              flex: 1,
              width: "2px",
              bgcolor: "divider",
              mt: 0.5,
              minHeight: 8,
            }}
          />
        </Stack>
      )}

      <Paper variant="outlined" sx={{ flex: 1, borderColor: "divider" }}>
        <Box
          sx={{
            p: 1.5,
            pb: 1.25,
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            cursor: "pointer",
          }}
          onClick={() => setOpen(!open)}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={400}>
              {rule.name}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              gap={0.5}
              mt={0.75}
              alignItems="center"
            >
              {rule.conditions.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  matches every lead
                </Typography>
              ) : (
                rule.conditions.map((c, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={conditionText(c)}
                    sx={{ bgcolor: "action.hover" }}
                  />
                ))
              )}
            </Stack>
          </Box>
          <Stack alignItems="flex-end" spacing={0.25} sx={{ flex: "none" }}>
            {statusChip(rule)}
            {total > 0 && (
              <Tooltip
                title={`Real outcome, not a goal: this rule took ${
                  took ?? 0
                } of ${total} routed leads in the selected range, counted from the routing log.`}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums", cursor: "help" }}
                >
                  took {Math.round(((took ?? 0) / total) * 100)}% of leads
                </Typography>
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" sx={{ flex: "none" }} onClick={stop}>
            {rule.hasUnpublishedChanges && (
              <Tooltip title="Make this rule live">
                <IconButton
                  size="small"
                  onClick={() => props.onPublish(rule.id)}
                >
                  <PublishIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {rule.published && (
              <Tooltip
                title={
                  rule.disabled
                    ? "Switch this rule back on"
                    : "Switch this rule off: kept, but the engine skips it"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => props.onToggleDisabled(rule)}
                >
                  {rule.disabled ? (
                    <PlayCircleOutlineIcon fontSize="small" />
                  ) : (
                    <PauseCircleOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              size="small"
              aria-label="edit rule"
              onClick={() => props.onEdit(rule)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="delete rule"
              onClick={() => props.onDelete(rule)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setOpen(!open)}
              sx={{
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {rule.holdLead ? (
            <Tooltip title="Matching leads are assigned to nobody — they wait for manual assignment and never fall to 'Everyone else'.">
              <Chip
                size="small"
                label="Holds leads — nobody is assigned"
                sx={chipTintSx("warn")}
              />
            </Tooltip>
          ) : (
            <SplitBar
              weights={resolveRuleSplit(rule, props.consultants)}
              consultantName={props.consultantName}
              colorFor={props.colorFor}
              faded={rule.disabled}
            />
          )}
        </Box>
        <Collapse in={open} unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1.5,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <RealSplitView
              ruleId={rule.id}
              from={props.from}
              to={props.to}
              weights={rule.weights}
              consultantName={props.consultantName}
            />
            <RoutedLeadsDisclosure
              ruleId={rule.id}
              from={props.from}
              to={props.to}
            />
          </Box>
        </Collapse>
      </Paper>
    </Stack>
  )
}

export function RulesPanel(props: { reloadKey?: number }) {
  const dispatch = useAppDispatch()
  const rulesState = useAppSelector(selectRules)
  const consultantsState = useAppSelector(selectRoutingConsultants)
  const [fallbackOpen, setFallbackOpen] = useState(false)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<Rule | undefined>(undefined)
  const [order, setOrder] = useState<Rule[]>([])

  useEffect(() => {
    if (rulesState.status === "success") {
      setOrder(
        rulesState.data.filter((rule) => !rule.isFallback && !rule.disabled),
      )
    }
  }, [rulesState])
  // Switched-off rules leave the match order; they live below Everyone else.
  const pausedRules =
    rulesState.status === "success"
      ? rulesState.data.filter(
          (rule) => rule.isFallback === false && rule.disabled,
        )
      : []

  // Real outcomes from the routing log: how many leads each rule's split
  // actually took in the chosen date range. Nothing here is simulated.
  const todayIso = new Date().toISOString().slice(0, 10)
  const monthAgoIso = new Date(Date.now() - 30 * 86400_000)
    .toISOString()
    .slice(0, 10)
  const [countFrom, setCountFrom] = useState(monthAgoIso)
  const [countTo, setCountTo] = useState(todayIso)
  const [leadCounts, setLeadCounts] = useState<RuleLeadCounts>()
  const loadCounts = useCallback(() => {
    if (!countFrom || !countTo || countFrom > countTo) return
    LeadRoutingClient.getRuleLeadCounts(countFrom, countTo)
      .then((counts) => {
        setLeadCounts(counts)
      })
      .catch(() => setLeadCounts(undefined))
    // props.reloadKey is intentional: bumping it from the global refresh
    // re-creates loadCounts so the effect below refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countFrom, countTo, props.reloadKey])
  useEffect(() => {
    loadCounts()
  }, [loadCounts])

  const sensors = useSensors(useSensor(PointerSensor))
  const consultants =
    consultantsState.status === "success" ? consultantsState.data : []
  const consultantName = (id: string) =>
    consultants.find((c) => c.id === id)?.name ?? "Unknown"
  const colorFor = (id: string): SplitHue => {
    const i = consultants.findIndex((c) => c.id === id)
    return splitHue(i < 0 ? 0 : i)
  }
  const ruleName = (id: string) => {
    const all = rulesState.status === "success" ? rulesState.data : []
    const rule = all.find((r) => r.id === id)
    return rule ? (rule.isFallback ? "Everyone else" : rule.name) : "a rule"
  }
  const fallback =
    rulesState.status === "success"
      ? rulesState.data.find((rule) => rule.isFallback)
      : undefined

  const tookByRule = new Map(
    (leadCounts?.rules ?? []).map((entry) => [entry.ruleId, entry.count]),
  )
  const total = leadCounts?.total ?? 0

  const openCreate = () => {
    setEditingRule(undefined)
    setEditorOpen(true)
  }
  const openEdit = (rule: Rule) => {
    setEditingRule(rule)
    setEditorOpen(true)
  }
  const onDelete = (rule: Rule) => {
    if (window.confirm(`Delete rule "${rule.name}"?`)) {
      dispatch(deleteRule(rule.id))
    }
  }
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = order.findIndex((rule) => rule.id === active.id)
      const newIndex = order.findIndex((rule) => rule.id === over.id)
      const next = arrayMove(order, oldIndex, newIndex)
      setOrder(next)
      // Paused rules keep trailing priorities so re-enabling lands them last,
      // never silently on top.
      dispatch(
        reorderRules([
          ...next.map((rule) => rule.id),
          ...pausedRules.map((rule) => rule.id),
        ]),
      )
    }
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="h6">Rules</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="The date range the took-% numbers on the cards are counted over.">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Counting leads from
              </Typography>
              <TextField
                type="date"
                size="small"
                value={countFrom}
                onChange={(e) => setCountFrom(e.target.value)}
                inputProps={{ max: countTo, style: { fontSize: 13 } }}
              />
              <Typography variant="caption" color="text.secondary">
                to
              </Typography>
              <TextField
                type="date"
                size="small"
                value={countTo}
                onChange={(e) => setCountTo(e.target.value)}
                inputProps={{
                  min: countFrom,
                  max: todayIso,
                  style: { fontSize: 13 },
                }}
              />
            </Stack>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            New rule
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 1,
          mb: 2,
          borderLeft: 3,
          borderColor: "primary.main",
          bgcolor: "action.hover",
        }}
      >
        <Typography variant="body2">
          Rules run top to bottom, first match wins. Drag to reorder.
        </Typography>
      </Paper>

      {rulesState.status === "error" && (
        <Alert severity="error">Failed to load routing rules.</Alert>
      )}

      {(rulesState.status === "idle" || rulesState.status === "pending") && (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      )}

      {rulesState.status === "success" && (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={order.map((rule) => rule.id)}
              strategy={verticalListSortingStrategy}
            >
              {order.map((rule, index) => (
                <SortableRuleCard
                  key={rule.id}
                  rule={rule}
                  index={index}
                  took={tookByRule.get(rule.id)}
                  total={total}
                  from={countFrom}
                  to={countTo}
                  consultantName={consultantName}
                  ruleName={ruleName}
                  colorFor={colorFor}
                  consultants={consultants}
                  onEdit={openEdit}
                  onDelete={onDelete}
                  onPublish={(id) => dispatch(publishRule(id))}
                  onToggleDisabled={(target) =>
                    dispatch(
                      setRuleDisabled({
                        id: target.id,
                        disabled: !target.disabled,
                      }),
                    )
                  }
                />
              ))}
            </SortableContext>
          </DndContext>

          {order.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: "center" }}
            >
              No rules yet, every lead goes to “Everyone else” below. Add one to
              start steering.
            </Typography>
          )}

          <Stack direction="row" mt={0.5}>
            <Stack alignItems="center" sx={{ width: 40, pt: 1.5 }}>
              <Tooltip title="Permanent catch-all, always last, can’t be deleted">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1.5,
                    bgcolor: "text.disabled",
                    color: "background.paper",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
            </Stack>
            <Paper
              variant="outlined"
              sx={{ flex: 1, borderStyle: "dashed", bgcolor: "action.hover" }}
            >
              <Box
                sx={{
                  p: 1.5,
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={400}>
                    Everyone else
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Catch-all · least-recently-assigned among all eligible
                    consultants
                  </Typography>
                </Box>
                {leadCounts && total > 0 && (
                  <Tooltip
                    title={`Real outcome: ${leadCounts.everyoneElse} of ${total} routed leads in the selected range matched no rule and fell through to this rotation.`}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        cursor: "help",
                      }}
                    >
                      took {Math.round((leadCounts.everyoneElse / total) * 100)}
                      % of leads
                    </Typography>
                  </Tooltip>
                )}
                {fallback && (
                  <IconButton
                    size="small"
                    onClick={() => setFallbackOpen((wasOpen) => !wasOpen)}
                    sx={{
                      transform: fallbackOpen ? "rotate(180deg)" : "none",
                      transition: "transform .15s",
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {fallback && fallback.weights.length > 0 && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <SplitBar
                    weights={fallback.weights}
                    consultantName={consultantName}
                    colorFor={colorFor}
                    faded
                  />
                </Box>
              )}
              {fallback && (
                <Collapse in={fallbackOpen} unmountOnExit>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.5,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <RealSplitView
                      ruleId="fallback"
                      from={countFrom}
                      to={countTo}
                      weights={[]}
                      consultantName={consultantName}
                    />
                    <RoutedLeadsDisclosure
                      ruleId="fallback"
                      from={countFrom}
                      to={countTo}
                    />
                  </Box>
                </Collapse>
              )}
            </Paper>
          </Stack>
          {pausedRules.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Tooltip title="Kept with their full setup, but the engine skips them. Switch one back on to re-join the match order at the bottom.">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ cursor: "help" }}
                >
                  Switched off · {pausedRules.length}
                </Typography>
              </Tooltip>
              <Stack spacing={1} mt={0.5}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                >
                  <SortableContext
                    items={pausedRules.map((rule) => rule.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {pausedRules.map((rule, index) => (
                      <SortableRuleCard
                        key={rule.id}
                        rule={rule}
                        index={order.length + index}
                        took={tookByRule.get(rule.id)}
                        total={total}
                        from={countFrom}
                        to={countTo}
                        consultantName={consultantName}
                        ruleName={ruleName}
                        colorFor={colorFor}
                        consultants={consultants}
                        onEdit={openEdit}
                        onDelete={onDelete}
                        onPublish={(id) => dispatch(publishRule(id))}
                        onToggleDisabled={(target) =>
                          dispatch(
                            setRuleDisabled({
                              id: target.id,
                              disabled: !target.disabled,
                            }),
                          )
                        }
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </Stack>
            </Box>
          )}
        </>
      )}

      <RuleEditorDialog
        open={editorOpen}
        rule={editingRule}
        onClose={() => setEditorOpen(false)}
      />
    </Box>
  )
}
